import React, { useEffect, useState } from 'react';
import { Bell, BellRing, BellOff } from 'lucide-react';
import {
  getPushState,
  enablePush,
  sendTestPush,
  PushState,
} from './push';

interface Props {
  userId: string | undefined;
  email: string | undefined;
  role: string | null;
  notify?: (msg: string) => void;
}

// Campanita de notificaciones push. Estados: sin permiso -> activar; activado ->
// toca para mandar una prueba; bloqueado -> avisa que hay que desbloquear en el navegador.
export default function PushBell({ userId, email, role, notify }: Props) {
  const [state, setState] = useState<PushState>('unsupported');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    getPushState().then(setState);
  }, []);

  if (state === 'unsupported') return null;

  const say = (m: string) => (notify ? notify(m) : undefined);

  const onClick = async () => {
    if (busy) return;
    if (state === 'denied') {
      say('Las notificaciones están bloqueadas. Habilitalas en los ajustes del navegador.');
      return;
    }
    setBusy(true);
    try {
      if (state === 'subscribed') {
        const r = await sendTestPush(email);
        say(r.ok ? 'Notificación de prueba enviada 🔔' : 'No se pudo enviar la prueba.');
      } else {
        const r = await enablePush(userId, email, role);
        if (r.ok) {
          setState('subscribed');
          say('Notificaciones activadas ✅ Te mandamos una de prueba.');
          await sendTestPush(email);
        } else if (r.reason === 'denied') {
          setState('denied');
          say('Permiso de notificaciones denegado.');
        } else {
          say('No se pudo activar: ' + (r.reason || 'error'));
        }
      }
    } finally {
      setBusy(false);
    }
  };

  const title =
    state === 'subscribed'
      ? 'Notificaciones activadas (tocar para probar)'
      : state === 'denied'
      ? 'Notificaciones bloqueadas'
      : 'Activar notificaciones';

  return (
    <button
      className="theme-toggle"
      onClick={onClick}
      aria-label={title}
      title={title}
      disabled={busy}
      style={state === 'subscribed' ? { color: '#10b981' } : undefined}
    >
      {state === 'subscribed' ? (
        <BellRing size={18} />
      ) : state === 'denied' ? (
        <BellOff size={18} />
      ) : (
        <Bell size={18} />
      )}
    </button>
  );
}
